import {
  ArrowLeft,
  FileText,
  Search,
  X,
  Plus,
  ChevronDown,
  Check,
  Video,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import Modal from "../../components/modal/Modal";
import images from "../../assets/images";
import { Training, Trainee } from "../../types/api";
import { useTrainingStore } from "../../store/store";
import { useS3Upload, getS3SignedUrl } from "../../utils/s3Client";
import { getUserDirectory, getDepartments } from "../../service/api";
import axiosInstance from "../../common/axiosInstance";
import { proxyUrl } from "../../common/utils";
import { usePremisesStore, useRegionStore } from "../../store/store";
import { useTrainingModule } from "./TrainingModuleContext";

interface CreateTrainingProps {
  mode: "create" | "edit" | "view";
}

interface JwtPayload {
  id?: string;
  sub?: string;
  roles?: string[];
  tenant_name?: string;
  [key: string]: any;
}

interface Instructor {
  id: string;
  name: string;
  employeeId: string;
}

interface TraineeWithPremise {
  traineeId: string;
  traineeEmployeeId: string;
  name: string;
  email?: string;
  department: string;
  role: string | { $oid: string };
  roleName?: string;
  attendance: {
    "Pre-Test": "Present" | "Absent";
    "Post-Test": "Present" | "Absent";
  };
  results?: {
    preTestId: string | null;
    postTestId: string | null;
    preTestScore: number | null;
    postTestScore: number | null;
  };
  premise: string[];
}

// File size limits
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB for documents
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB for videos

const isVideoFile = (fileName: string): boolean => {
  const videoExtensions = [
    ".mp4",
    ".avi",
    ".mov",
    ".wmv",
    ".flv",
    ".webm",
    ".mkv",
    ".m4v",
  ];
  const ext = fileName.toLowerCase().substring(fileName.lastIndexOf("."));
  return videoExtensions.includes(ext);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (totalMinutes: number): string => {
  const m = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// Wraps past midnight when the end time is earlier than the start time.
const computeDurationMinutes = (start: string, end: string): number => {
  const diff = timeToMinutes(end) - timeToMinutes(start);
  return diff <= 0 ? diff + 1440 : diff;
};

const CreateTraining: React.FC<CreateTrainingProps> = ({ mode }) => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { moduleType, validCategories, defaultCategory, paths } =
    useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(50);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialFiles, setInitialFiles] = useState<File[]>([]);
  const [tenantId, setTenantId] = useState<string>("");
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [training, setTraining] = useState<Training>({
    id: "",
    topic: "",
    category: isFixedCategory ? defaultCategory : "",
    date: "",
    duration: "",
    instructor: [],
    method: "",
    files: [],
    trainees: [],
    status: "Active",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tenantId: "",
    moduleType,
  });
  // TraineeAttendance state
  const [selectedTrainees, setSelectedTrainees] = useState<Trainee[]>([]);
  const [availableTrainees, setAvailableTrainees] = useState<
    TraineeWithPremise[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [departmentMap, setDepartmentMap] = useState<Map<string, string>>(
    new Map(),
  );
  const listPerPage = 5;
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [premisesDropdownOpen, setPremisesDropdownOpen] = useState(false);
  const [regionsDropdownOpen, setRegionsDropdownOpen] = useState(false);
  const [filteredInstructors, setFilteredInstructors] = useState<Instructor[]>(
    [],
  );
  const [instructorDropdownOpen, setInstructorDropdownOpen] = useState(false);
  const [allTrainees, setAllTrainees] = useState<TraineeWithPremise[]>([]);

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();
  const {
    createTraining: createTrainingStore,
    updateTraining: updateTrainingStore,
    fetchTrainingById,
  } = useTrainingStore();
  const isViewMode = mode === "view";

  // Fetch tenantId
  useEffect(() => {
    const tenantId = user?.tenant_id;
    if (tenantId) {
      setTenantId(tenantId);
      setTraining((prev) => ({ ...prev, tenantId: tenantId }));
    }
  }, [user]);

  // Load the existing training's data when editing or viewing
  useEffect(() => {
    if (mode === "create" || !id) return;
    let cancelled = false;
    const loadExistingTraining = async () => {
      try {
        // fetchTrainingById resolves to the raw API envelope
        // ({ success, data: Training }), not a flat Training object,
        // despite its declared return type — unwrap .data here.
        const rawResponse = await fetchTrainingById(id);
        const existingTraining = (
          rawResponse as unknown as { success: boolean; data: Training }
        )?.data;
        if (cancelled || !existingTraining) return;
        setTraining((prev) => ({
          ...prev,
          ...existingTraining,
          date: existingTraining.date
            ? new Date(existingTraining.date).toISOString().split("T")[0]
            : "",
          instructor:
            existingTraining.instructor?.length > 0
              ? existingTraining.instructor
              : prev.instructor,
        }));
        const existingPremises = existingTraining.premises || [];
        setSelectedPremises(
          existingPremises.length === 0 ? ["ALL"] : existingPremises,
        );
        const existingRegions = existingTraining.regions || [];
        setSelectedRegions(
          existingRegions.length === 0 ? ["ALL"] : existingRegions,
        );
        if (existingTraining.startTime && existingTraining.endTime) {
          setStartTime(existingTraining.startTime);
          setEndTime(existingTraining.endTime);
        } else {
          // Legacy record: only the total minutes were persisted, so
          // hydrate a default 09:00 start and derive the end time from
          // the stored duration.
          const defaultStart = "09:00";
          const existingDurationMinutes =
            parseInt(existingTraining.duration || "0", 10) || 0;
          setStartTime(defaultStart);
          setEndTime(
            minutesToTime(
              timeToMinutes(defaultStart) + existingDurationMinutes,
            ),
          );
        }
      } catch (err) {
        console.error("Error loading training for edit/view:", err);
        setError("Failed to load training data");
      }
    };
    loadExistingTraining();
    return () => {
      cancelled = true;
    };
  }, [mode, id, fetchTrainingById]);

  // Fetch instructors, trainees, and departments
  useEffect(() => {
    setSelectedTrainees(training.trainees || []);
    setSelectedIds(
      (training.trainees || []).map((trainee) => trainee.traineeId),
    );

    const fetchAdditionalTrainees = async () => {
      try {
        const response = await getUserDirectory();
        const users = Array.isArray(response.data.data)
          ? response.data.data
          : [];

        // Populate instructors with orgusers
        const orgUsers = users
          .filter((user) => user.roles?.includes("orguser") && !user.isDeleted)
          .map((user) => ({
            id: user._id,
            name:
              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
              "Unknown",
            employeeId: user.employeeId || "",
            email: user.email || "",
            premise: user.premise, // Add premise here
          }));
        setInstructors(orgUsers);

        const departmentIds = users
          .map((user) => {
            if (
              user.department &&
              typeof user.department === "object" &&
              "$oid" in user.department
            ) {
              return user.department.$oid;
            } else if (typeof user.department === "string") {
              return user.department;
            }
            return null;
          })
          .filter((id): id is string => !!id);
        const uniqueDepartmentIds = Array.from(new Set(departmentIds));
        const deptResponse = await getDepartments(uniqueDepartmentIds);
        const departmentsRaw = deptResponse.data.data || [];
        const departments = Array.isArray(departmentsRaw) ? departmentsRaw : [];
        const deptMap = new Map(
          departments.map(
            (dept: {
              _id: string | { $oid: string };
              departmentName: string;
            }) => [
              typeof dept._id === "object" &&
              dept._id !== null &&
              "$oid" in dept._id
                ? dept._id.$oid
                : typeof dept._id === "string"
                  ? dept._id
                  : "",
              dept.departmentName,
            ],
          ),
        );
        setDepartmentMap(deptMap);

        const roleDetails = await Promise.all(
          users.map(async (user) => {
            const roleId =
              typeof user.role === "string" ? user.role : user.role?.$oid || "";
            if (roleId) {
              try {
                const roleResponse = await axiosInstance.get(
                  `/api/roles/${roleId}`
                );
                return {
                  id: user._id,
                  roleId,
                  roleName: roleResponse.data.message.roleName || "Unknown",
                };
              } catch (error) {
                console.error(
                  `Failed to fetch role for user ${user._id}:`,
                  error,
                );
                return { id: user._id, roleId, roleName: "Unknown" };
              }
            }
            return { id: user._id, roleId, roleName: "Unknown" };
          }),
        );

        const roleMap = new Map(roleDetails.map((r) => [r.roleId, r.roleName]));
        const filteredTrainees: TraineeWithPremise[] = users
          .filter((user) => {
            const hasOrgUser = user.roles?.includes("orguser") || false;
            const isAllowedRole =
              userType === "orguser"
                ? user.roles?.some((r) => ["orguser"].includes(r))
                : true;
            const isNotDeleted = !user.isDeleted;
            // Exclude all selected instructors, not just the first one
            const selectedInstructorIds = training.instructor.map(
              (inst) => inst.trainerId,
            );
            const isNotInstructor = !selectedInstructorIds.includes(user._id);
            return (
              hasOrgUser && isAllowedRole && isNotDeleted && isNotInstructor
            );
          })
          .map((user) => {
            let departmentId = "";
            if (
              user.department &&
              typeof user.department === "object" &&
              "$oid" in user.department
            ) {
              departmentId = user.department.$oid;
            } else if (typeof user.department === "string") {
              departmentId = user.department;
            }
            const departmentName = deptMap.get(departmentId) || "Unknown";
            const roleId =
              typeof user.role === "object" && user.role?.$oid
                ? user.role.$oid
                : user.role || "";
            const roleIdStr =
              typeof roleId === "object" && roleId !== null && "$oid" in roleId
                ? roleId.$oid
                : roleId;
            const roleName = roleMap.get(roleIdStr) || "Unknown";
            // Normalize premise to string[]
            let premiseArr: string[] = [];
            if (Array.isArray(user.premise)) {
              premiseArr = user.premise.map((p: any) =>
                typeof p === "string" ? p : p.$oid,
              );
            } else if (typeof user.premise === "string") {
              premiseArr = [user.premise];
            }
            return {
              traineeId: user._id,
              traineeEmployeeId: user.employeeId,
              name:
                `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                "Unknown",
              email: user.email || "",
              department: departmentId,
              role: roleId,
              roleName,
              attendance: { "Pre-Test": "Absent", "Post-Test": "Absent" },
              results: {
                preTestId: null,
                postTestId: null,
                preTestScore: null,
                postTestScore: null,
              },
              premise: premiseArr,
            };
          });

        setAllTrainees(filteredTrainees);
        setAvailableTrainees(filteredTrainees);
      } catch (err) {
        console.error("Error fetching additional trainees:", err);
        setAvailableTrainees([]);
      }
    };
    fetchAdditionalTrainees();
  }, [training.trainees, userType, training.instructor]);

  useEffect(() => {
    fetchPremises();
    fetchRegions();
  }, [fetchPremises, fetchRegions]);

  // Filter trainees and instructors by selected premises
  useEffect(() => {
    if (selectedPremises.length === 0 || selectedPremises.includes("ALL")) {
      setAvailableTrainees(allTrainees);
      setFilteredInstructors(instructors);
    } else {
      setAvailableTrainees(
        allTrainees.filter(
          (trainee: TraineeWithPremise) =>
            trainee.premise &&
            trainee.premise.some((premId: string) =>
              selectedPremises.includes(premId),
            ),
        ),
      );
      setFilteredInstructors(
        instructors.filter((inst: any) => {
          if (!inst.premise) return false;
          // Normalize premise to array
          const premiseArr = Array.isArray(inst.premise)
            ? inst.premise.map((p: any) => (typeof p === "string" ? p : p.$oid))
            : typeof inst.premise === "string"
              ? [inst.premise]
              : [];
          return premiseArr.some((premId: string) =>
            selectedPremises.includes(premId),
          );
        }),
      );
    }
  }, [selectedPremises, instructors, allTrainees]);

  // Handle click outside for dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const dropdown = document.getElementById("premises-dropdown-menu");
      const button = document.getElementById("premises-dropdown-btn");
      if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        button &&
        !button.contains(event.target as Node)
      ) {
        setPremisesDropdownOpen(false);
      }
    }
    if (premisesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [premisesDropdownOpen]);

  // Handle click outside for regions dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const dropdown = document.getElementById("regions-dropdown-menu");
      const button = document.getElementById("regions-dropdown-btn");
      if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        button &&
        !button.contains(event.target as Node)
      ) {
        setRegionsDropdownOpen(false);
      }
    }
    if (regionsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [regionsDropdownOpen]);

  // Add click outside handler for instructor dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const dropdown = document.getElementById("instructor-dropdown-menu");
      const button = document.getElementById("instructor-dropdown-btn");
      if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        button &&
        !button.contains(event.target as Node)
      ) {
        setInstructorDropdownOpen(false);
      }
    }
    if (instructorDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [instructorDropdownOpen]);

  const onDrop = async (acceptedFiles: File[], rejectedFiles: any[] = []) => {
    if (isViewMode) return;

    // Handle rejected files (size/type issues)
    if (rejectedFiles.length > 0) {
      const rejectedMessages = rejectedFiles.map(({ file, errors }) => {
        const isVideo = isVideoFile(file.name);
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
        const maxSizeFormatted = formatFileSize(maxSize);

        if (errors.some((e: any) => e.code === "file-too-large")) {
          return `${file.name}: File size exceeds ${maxSizeFormatted} limit`;
        }
        if (errors.some((e: any) => e.code === "file-invalid-type")) {
          return `${file.name}: Invalid file type`;
        }
        return `${file.name}: ${errors.map((e: any) => e.message).join(", ")}`;
      });
      setError(`Upload failed: ${rejectedMessages.join("; ")}`);
      return;
    }

    // Validate file sizes before upload
    const invalidFiles = acceptedFiles.filter((file) => {
      const isVideo = isVideoFile(file.name);
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
      return file.size > maxSize;
    });

    if (invalidFiles.length > 0) {
      const messages = invalidFiles.map((file) => {
        const isVideo = isVideoFile(file.name);
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
        const maxSizeFormatted = formatFileSize(maxSize);
        return `${file.name}: File size (${formatFileSize(
          file.size,
        )}) exceeds ${maxSizeFormatted} limit`;
      });
      setError(`Upload failed: ${messages.join("; ")}`);
      return;
    }

    try {
      const uploadedFiles = await Promise.all(
        acceptedFiles.map(async (file) => {
          const url = await uploadFileToS3(file, {});
          return url;
        }),
      );

      setTraining({
        ...training,
        files: [...training.files, ...uploadedFiles],
      });
      setError(null); // Clear any previous errors on success
    } catch (err) {
      setError("Failed to upload files");
      console.error("File upload error:", err);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "video/mp4": [".mp4", ".m4v"],
      "video/x-msvideo": [".avi"],
      "video/quicktime": [".mov"],
      "video/x-ms-wmv": [".wmv"],
      "video/x-flv": [".flv"],
      "video/webm": [".webm"],
      "video/x-matroska": [".mkv"],
    },
    maxSize: MAX_VIDEO_SIZE, // Set to max video size, per-file validation happens in onDrop/validator
    disabled: isViewMode,
    validator: (file) => {
      const isVideo = isVideoFile(file.name);
      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
      if (file.size > maxSize) {
        return {
          code: "file-too-large",
          message: `File size exceeds ${formatFileSize(maxSize)} limit`,
        };
      }
      return null;
    },
  });

  const handleNext = () => {
    if (step < 2) {
      setStep(step + 1);
      setProgress(progress + 25);
      setError(null);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
      setProgress(progress - 25);
      setError(null);
    }
  };

  const handleTraineeUpdate = useCallback(
    (trainees: Trainee[]) => {
      if (isViewMode) return;
      const validTrainees = trainees.filter(
        (trainee) =>
          trainee.traineeId &&
          trainee.name &&
          trainee.department &&
          trainee.role,
      );
      setTraining((prev) => ({
        ...prev,
        trainees: validTrainees.map((trainee) => ({
          ...trainee,
          attendance: trainee.attendance || "Absent",
        })),
      }));
    },
    [isViewMode],
  );

  const handleSubmit = async () => {
    if (isViewMode) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const { topic, category, date, duration, instructor, trainees } =
        training;

      if (!validCategories.includes(category)) {
        setStep(1);
        setProgress(50);
        throw new Error("Please select a valid category");
      }

      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        setStep(1);
        setProgress(50);
        throw new Error("Please select a valid date in YYYY-MM-DD format");
      }

      if (!topic || !category || !date || instructor.length === 0 || !duration) {
        setStep(1);
        setProgress(50);
        throw new Error("Please fill in all required fields");
      }

      if (selectedRegions.length === 0) {
        setStep(1);
        setProgress(50);
        throw new Error("Please select at least one region");
      }

      if (selectedPremises.length === 0) {
        setStep(1);
        setProgress(50);
        throw new Error("Please select at least one premises");
      }

      if (trainees.length === 0) {
        setStep(2);
        setProgress(75);
        throw new Error("Please select at least one trainee");
      }

      if (instructor.some((inst) => !inst.trainerId)) {
        setStep(1);
        setProgress(50);
        throw new Error("Every selected instructor must be valid");
      }

      const trainingData: Partial<Training> = {
        ...training,
        category: isFixedCategory ? defaultCategory : category,
        moduleType,
        startTime,
        endTime,
        method: training.method || undefined,
        files: training.files,
        trainees: training.trainees.map((t) => ({
          traineeId: t.traineeId,
          traineeEmployeeId: t.traineeEmployeeId,
          name: t.name,
          email: t.email || "",
          department: t.department,
          role:
            typeof t.role === "object" && t.role?.$oid ? t.role.$oid : t.role,
          roleName: t.roleName,
          attendance: t.attendance || "Absent",
          results: t.results || {
            preTestId: null,
            postTestId: null,
            preTestScore: null,
            postTestScore: null,
          },
        })),
        status: training.status || "Active",
        tenantId: training.tenantId,
        instructor: training.instructor.map((inst) => ({
          trainerId: inst.trainerId,
          trainerEmployeeId: inst.trainerEmployeeId,
          trainerName: inst.trainerName,
        })),
        premises: selectedPremises.includes("ALL") ? [] : selectedPremises,
        regions: selectedRegions.includes("ALL") ? [] : selectedRegions,
      };
      const result =
        mode === "edit" && id
          ? await updateTrainingStore(id, trainingData)
          : await createTrainingStore(trainingData as Omit<Training, "id">);
      setIsModalOpen(true);
    } catch (error: any) {
      let errorMessage = "Failed to submit training";
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
      console.error("Submission failed:", {
        errorMessage,
        errorDetails: error.stack || error,
        trainingData: JSON.stringify(training, null, 2),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    if (isViewMode) return;
    const newFiles = [...training.files];
    newFiles.splice(index, 1);
    setTraining({ ...training, files: newFiles });
  };

  const handleViewFile = async (file: string) => {
    try {
      const fileurl = await getS3SignedUrl(file, "application/octet-stream");
      window.open(fileurl, "_blank");
    } catch (err) {
      setError("Failed to view file");
      console.error("File view error:", err);
    }
  };

  const handleModalContinue = () => {
    setIsModalOpen(false);
    navigate(paths.dashboard);
  };

  // TraineeAttendance functions
  const indexOfLastItem = currentPage * listPerPage;
  const indexOfFirstItem = indexOfLastItem - listPerPage;
  const currentItems = selectedTrainees.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(selectedTrainees.length / listPerPage) || 1;

  const addMultipleTrainees = () => {
    if (isViewMode) return;
    const traineesToAdd = availableTrainees.filter(
      (trainee) =>
        tempSelectedIds.includes(trainee.traineeId) &&
        !selectedIds.includes(trainee.traineeId),
    );

    if (traineesToAdd.length > 0) {
      const newTrainees = [...selectedTrainees, ...traineesToAdd].map((t) => ({
        ...t,
        premise: Array.isArray(t.premise) ? t.premise[0] : t.premise, // or omit if not needed
      }));
      setSelectedTrainees(newTrainees);
      setSelectedIds([...selectedIds, ...tempSelectedIds]);
      setTempSelectedIds([]);
      handleTraineeUpdate(newTrainees);
    }
  };

  const removeTrainee = (id: string) => {
    if (isViewMode) return;
    const newTrainees = selectedTrainees.filter(
      (trainee) => trainee.traineeId !== id,
    );
    setSelectedTrainees(newTrainees);
    setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    handleTraineeUpdate(newTrainees);
  };

  const toggleTempSelection = (id: string) => {
    if (isViewMode) return;
    setTempSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((selectedId) => selectedId !== id)
        : [...prev, id],
    );
  };

  const filteredTrainees = availableTrainees.filter(
    (trainee) =>
      trainee.traineeId.includes(searchTerm) ||
      trainee.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const isStep1Valid =
    training.topic &&
    validCategories.includes(training.category) &&
    training.date &&
    /^\d{4}-\d{2}-\d{2}$/.test(training.date) &&
    training.instructor.length > 0 &&
    training.instructor.every(
      (inst) => inst.trainerId && inst.trainerName && inst.trainerEmployeeId,
    ) &&
    training.duration &&
    selectedRegions.length > 0 &&
    selectedPremises.length > 0;

  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-blue-800">
          {mode === "create" ? "Create" : mode === "edit" ? "Edit" : "View"}{" "}
          Training Plan
        </h2>
        <Link to={paths.dashboard}>
          <button className="flex items-center gap-2 bg-[#61748e] text-white px-4 py-2 rounded-full">
            <ArrowLeft /> Back
          </button>
        </Link>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2.5 my-4">
        <div
          className="bg-[#0fa9a6] h-2.5 rounded-full"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {step === 1 && (
        <form
          className="relative bg-white p-6 mt-6 rounded-lg shadow-md"
          encType="multipart/form-data"
        >
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Session Details
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Topic *</label>
              <input
                type="text"
                className={`border-2 border-[#a5b7cf] p-2 rounded ${
                  isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={training.topic}
                onChange={(e) => {
                  if (mode !== "view") {
                    setTraining({ ...training, topic: e.target.value });
                  }
                }}
                required
                disabled={isViewMode}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">Category *</label>
              {isFixedCategory ? (
                <input
                  type="text"
                  className="border-2 border-[#a5b7cf] p-2 rounded bg-gray-100 cursor-not-allowed"
                  value={defaultCategory}
                  readOnly
                />
              ) : (
                <select
                  className={`border-2 border-[#a5b7cf] p-2 rounded ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  value={training.category}
                  onChange={(e) =>
                    mode !== "view" &&
                    setTraining({ ...training, category: e.target.value })
                  }
                  required
                  disabled={isViewMode}
                >
                  <option value="" disabled hidden>
                    Select Category
                  </option>
                  {validCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Training Date *
              </label>
              <input
                type="date"
                className={`border-2 border-[#a5b7cf] p-2 rounded ${
                  isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={training.date}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => {
                  if (mode !== "view") {
                    const dateValue = e.target.value;
                    setTraining({ ...training, date: dateValue });
                  }
                }}
                required
                disabled={isViewMode}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold">
                Training Time *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  className={`border-2 border-[#a5b7cf] p-2 rounded ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  value={startTime}
                  onChange={(e) => {
                    if (mode === "view") return;
                    const newStart = e.target.value;
                    setStartTime(newStart);
                    if (newStart && endTime) {
                      setTraining((prev) => ({
                        ...prev,
                        duration: String(
                          computeDurationMinutes(newStart, endTime),
                        ),
                      }));
                    }
                  }}
                  required
                  disabled={isViewMode}
                />
                <span className="text-gray-500">-</span>
                <input
                  type="time"
                  className={`border-2 border-[#a5b7cf] p-2 rounded ${
                    isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  value={endTime}
                  onChange={(e) => {
                    if (mode === "view") return;
                    const newEnd = e.target.value;
                    setEndTime(newEnd);
                    if (startTime && newEnd) {
                      setTraining((prev) => ({
                        ...prev,
                        duration: String(
                          computeDurationMinutes(startTime, newEnd),
                        ),
                      }));
                    }
                  }}
                  required
                  disabled={isViewMode}
                />
              </div>
            </div>
            {/* Region Multi-Select Custom Dropdown (narrows the Premises multi-select below) */}
            <div className="flex flex-col col-span-1">
              <label className="text-gray-500 font-semibold">Region *</label>
              <div className="relative">
                <button
                  type="button"
                  id="regions-dropdown-btn"
                  className="border-2 border-[#a5b7cf] p-2 rounded w-full flex justify-between items-center bg-white"
                  onClick={() => setRegionsDropdownOpen((open) => !open)}
                  disabled={isViewMode}
                >
                  <span className="truncate text-left flex-1">
                    {selectedRegions.length === 0
                      ? "Select Region(s)"
                      : selectedRegions.includes("ALL")
                        ? "All Regions"
                        : selectedRegions.length === 1
                          ? regions.find(
                              (r: any) => r._id === selectedRegions[0],
                            )?.regionName || "1 selected"
                          : `${selectedRegions.length} selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
                </button>
                {regionsDropdownOpen && (
                  <div
                    id="regions-dropdown-menu"
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                  >
                    <div
                      className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                      onClick={() => {
                        setSelectedRegions(["ALL"]);
                        setSelectedPremises([]);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRegions.includes("ALL")}
                        readOnly
                        className="mr-2"
                      />
                      <span>All Regions</span>
                      {selectedRegions.includes("ALL") && (
                        <Check className="w-4 h-4 text-green-500 ml-auto" />
                      )}
                    </div>
                    {regions.map((region: any) => (
                      <div
                        key={region._id}
                        className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                        onClick={() => {
                          let newSelected = selectedRegions.filter(
                            (r) => r !== "ALL",
                          );
                          if (newSelected.includes(region._id)) {
                            newSelected = newSelected.filter(
                              (r) => r !== region._id,
                            );
                          } else {
                            newSelected.push(region._id);
                          }
                          setSelectedRegions(newSelected);
                          setSelectedPremises([]);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedRegions.includes("ALL")
                              ? false
                              : selectedRegions.includes(region._id)
                          }
                          readOnly
                          className="mr-2"
                        />
                        <span>{region.regionName}</span>
                        {selectedRegions.includes(region._id) &&
                          !selectedRegions.includes("ALL") && (
                            <Check className="w-4 h-4 text-green-500 ml-auto" />
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Premises Multi-Select Custom Dropdown (moved up) */}
            <div className="flex flex-col col-span-1">
              <label className="text-gray-500 font-semibold">Premises *</label>
              <div className="relative">
                <button
                  type="button"
                  id="premises-dropdown-btn"
                  className="border-2 border-[#a5b7cf] p-2 rounded w-full flex justify-between items-center bg-white"
                  onClick={() => setPremisesDropdownOpen((open) => !open)}
                  disabled={isViewMode}
                >
                  <span className="truncate text-left flex-1">
                    {selectedPremises.length === 0 ||
                    selectedPremises.includes("ALL")
                      ? "All Premises"
                      : selectedPremises.length === 1
                        ? allPremises.find(
                            (p: any) => p.id === selectedPremises[0],
                          )?.premisesName || "1 selected"
                        : `${selectedPremises.length} selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
                </button>
                {premisesDropdownOpen && (
                  <div
                    id="premises-dropdown-menu"
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                  >
                    <div
                      className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                      onClick={() => setSelectedPremises(["ALL"])}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPremises.includes("ALL")}
                        readOnly
                        className="mr-2"
                      />
                      <span>All Premises</span>
                      {selectedPremises.includes("ALL") && (
                        <Check className="w-4 h-4 text-green-500 ml-auto" />
                      )}
                    </div>
                    {allPremises
                      .filter((premise: any) =>
                        selectedRegions.length > 0 &&
                        !selectedRegions.includes("ALL")
                          ? selectedRegions.includes(premise.region)
                          : true,
                      )
                      .map((premise: any) => (
                      <div
                        key={premise.id || premise._id}
                        className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                        onClick={() => {
                          let newSelected = [...selectedPremises];
                          if (selectedPremises.includes("ALL"))
                            newSelected = [];
                          if (newSelected.includes(premise.id)) {
                            newSelected = newSelected.filter(
                              (p) => p !== premise.id,
                            );
                          } else {
                            newSelected.push(premise.id);
                          }
                          setSelectedPremises(newSelected);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedPremises.includes("ALL")
                              ? false
                              : selectedPremises.includes(premise.id)
                          }
                          readOnly
                          className="mr-2"
                        />
                        <span>{premise.premisesName}</span>
                        {selectedPremises.includes(premise.id) &&
                          !selectedPremises.includes("ALL") && (
                            <Check className="w-4 h-4 text-green-500 ml-auto" />
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col col-span-1">
              <label className="text-gray-500 font-semibold">
                Instructor(s) *
              </label>
              {/* --- Instructor Multi-Select Custom Dropdown --- */}
              <div className="relative">
                <button
                  type="button"
                  id="instructor-dropdown-btn"
                  className="border-2 border-[#a5b7cf] p-2 rounded w-full flex justify-between items-center bg-white"
                  onClick={() => setInstructorDropdownOpen((open) => !open)}
                  disabled={isViewMode}
                >
                  <span className="truncate text-left flex-1">
                    {training.instructor.length === 0
                      ? "Select Instructor(s)"
                      : training.instructor.length === 1
                        ? training.instructor[0].trainerName
                        : `${training.instructor.length} selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500 ml-2" />
                </button>
                {instructorDropdownOpen && (
                  <div
                    id="instructor-dropdown-menu"
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredInstructors.length === 0 ? (
                      <div className="px-2 py-2 text-gray-500 text-sm">
                        No instructors available
                      </div>
                    ) : (
                      filteredInstructors.map((instructor) => {
                        const isChecked = training.instructor.some(
                          (inst) => inst.trainerId === instructor.id,
                        );
                        return (
                          <div
                            key={instructor.id}
                            className="px-2 py-1 hover:bg-gray-100 cursor-pointer flex items-center"
                            onClick={() => {
                              if (isViewMode) return;
                              const newInstructors = isChecked
                                ? training.instructor.filter(
                                    (inst) =>
                                      inst.trainerId !== instructor.id,
                                  )
                                : [
                                    ...training.instructor,
                                    {
                                      trainerId: instructor.id,
                                      trainerEmployeeId:
                                        instructor.employeeId,
                                      trainerName: instructor.name,
                                    },
                                  ];
                              setTraining({
                                ...training,
                                instructor: newInstructors,
                              });
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              readOnly
                              className="mr-2"
                            />
                            <span>
                              {instructor.name} ({instructor.employeeId})
                            </span>
                            {isChecked && (
                              <Check className="w-4 h-4 text-green-500 ml-auto" />
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col col-span-1">
              <label className="text-gray-500 font-semibold">Method *</label>
              <input
                type="text"
                className={`border-2 border-[#a5b7cf] p-2 rounded ${
                  isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={training.method}
                onChange={(e) =>
                  mode !== "view" &&
                  setTraining({ ...training, method: e.target.value })
                }
                disabled={isViewMode}
              />
            </div>
          </div>

          <div className="flex flex-col mt-6">
            <div className="flex justify-between items-center">
              <label className="text-gray-500 font-semibold">
                Upload Files
              </label>
              {mode !== "view" && (
                <p className="text-xs text-gray-500 font-semibold">
                  Supported formats:{" "}
                  <span className="text-orange-500">
                    Documents (DOC/DOCX, PDF, TXT, JPG, PNG - Max:{" "}
                    {formatFileSize(MAX_FILE_SIZE)})
                  </span>
                  {" | "}
                  <span className="text-blue-500">
                    Videos (MP4, AVI, MOV, WMV, FLV, WEBM, MKV, M4V - Max:{" "}
                    {formatFileSize(MAX_VIDEO_SIZE)})
                  </span>
                </p>
              )}
            </div>
            <div
              className={`border-2 border-[#a5b7cf] p-4 rounded-lg ${
                isViewMode ? "" : "border-dashed"
              } text-center`}
              {...(mode !== "view" ? getRootProps() : {})}
            >
              {mode !== "view" && <input {...getInputProps()} />}
              {isViewMode ? (
                <p className="text-gray-500 font-semibold">Attached Files</p>
              ) : (
                <p className="text-gray-500 font-semibold">
                  Drag & Drop Your Files Here or Click
                </p>
              )}

              <div className="flex gap-4 mt-4 flex-wrap">
                {training.files.map((file, index) => {
                  const fileName = file.split("/").pop() || "";
                  const isVideo = isVideoFile(fileName);
                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center bg-gray-100 p-3 rounded-md w-24 relative group"
                    >
                      {isVideo ? (
                        <Video size={32} className="text-red-500" />
                      ) : (
                        <FileText size={32} className="text-blue-500" />
                      )}
                      <p
                        className="text-xs text-gray-600 truncate w-full text-center"
                        title={fileName}
                      >
                        {fileName}
                      </p>
                      {isVideo && (
                        <span className="text-xs text-red-500 font-semibold mt-1">
                          Video
                        </span>
                      )}
                      {mode !== "view" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                          title="Remove file"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
                {training.files.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {isViewMode ? "No files attached" : "No files selected"}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </form>
      )}

      {step === 2 && (
        <div className="p-6 bg-white rounded shadow">
          <h2 className="text-xl font-semibold mb-4">Trainee Attendance</h2>
          <div className="flex gap-4 flex-col md:flex-row">
            <div className="border p-4 rounded-lg w-[50%] md:w-1/2">
              <div className="mb-4">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 whitespace-nowrap">
                  {selectedTrainees.length > 0 ? (
                    selectedTrainees.map((trainee: Trainee) => (
                      <span
                        key={trainee.traineeId}
                        className="bg-gray-200 px-2 py-1 rounded text-sm flex items-center flex-shrink-0"
                      >
                        {trainee.name}
                        {!isViewMode && (
                          <button
                            className="ml-2 text-red-500"
                            onClick={() => removeTrainee(trainee.traineeId)}
                            title="Remove trainee"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">
                      No trainees selected
                    </span>
                  )}
                </div>
              </div>

              {!isViewMode && (
                <>
                  <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search Trainees"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="border rounded w-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Select All Checkbox and Add Selected Button */}
                  <div className="flex items-center mb-2 gap-80">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="select-all-trainees"
                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                        checked={
                          filteredTrainees.length > 0 &&
                          filteredTrainees.every(
                            (trainee) =>
                              tempSelectedIds.includes(trainee.traineeId) ||
                              selectedIds.includes(trainee.traineeId),
                          )
                        }
                        disabled={filteredTrainees.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Select all filtered trainees not already selected
                            const toAdd = filteredTrainees
                              .filter(
                                (trainee) =>
                                  !selectedIds.includes(trainee.traineeId) &&
                                  !tempSelectedIds.includes(trainee.traineeId),
                              )
                              .map((trainee) => trainee.traineeId);
                            setTempSelectedIds((prev) => [...prev, ...toAdd]);
                          } else {
                            // Deselect all filtered trainees from temp selection
                            setTempSelectedIds((prev) =>
                              prev.filter(
                                (id) =>
                                  !filteredTrainees.some(
                                    (trainee) => trainee.traineeId === id,
                                  ),
                              ),
                            );
                          }
                        }}
                      />
                      <label
                        htmlFor="select-all-trainees"
                        className="text-sm font-medium text-gray-700 select-none"
                      >
                        Select All
                      </label>
                    </div>
                    <button
                      onClick={addMultipleTrainees}
                      disabled={tempSelectedIds.length === 0 || isViewMode}
                      className="flex items-center gap-2 bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      type="button"
                    >
                      <Plus size={16} />
                      Add Selected ({tempSelectedIds.length})
                    </button>
                  </div>

                  <div className="max-h-60 overflow-y-auto border rounded">
                    {filteredTrainees.length > 0 ? (
                      <>
                        {filteredTrainees.map((trainee: TraineeWithPremise) => (
                          <div
                            key={trainee.traineeId}
                            className="p-2 hover:bg-gray-50 flex items-center"
                          >
                            <input
                              type="checkbox"
                              checked={
                                tempSelectedIds.includes(trainee.traineeId) ||
                                selectedIds.includes(trainee.traineeId)
                              }
                              onChange={() =>
                                toggleTempSelection(trainee.traineeId)
                              }
                              disabled={
                                selectedIds.includes(trainee.traineeId) ||
                                isViewMode
                              }
                              className="mr-2 h-4 w-4 text-blue-600 rounded disabled:opacity-50"
                            />
                            <span
                              className={
                                selectedIds.includes(trainee.traineeId)
                                  ? "text-gray-400"
                                  : ""
                              }
                            >
                              {trainee.name}-{trainee.traineeEmployeeId}-
                              {departmentMap.get(trainee.department) ||
                                trainee.department}
                              -{trainee.roleName}
                              {selectedIds.includes(trainee.traineeId)}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        No trainees found
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="flex flex-col gap-4 w-full md:w-1/2">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-100">
                      <th className="border p-2">Trainee Name</th>
                      <th className="border p-2">Department</th>
                      <th className="border p-2">Role</th>
                      {!isViewMode && <th className="border p-2">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((trainee: Trainee) => (
                        <tr key={trainee.traineeId} className="border">
                          <td className="border p-2">
                            {trainee.name}-{trainee.traineeEmployeeId}
                          </td>
                          <td className="border p-2">
                            {departmentMap.get(trainee.department) ||
                              trainee.department}
                          </td>
                          <td className="border p-2">
                            {trainee.roleName ||
                              (typeof trainee.role === "object"
                                ? trainee.role.$oid
                                : trainee.role)}
                          </td>
                          {!isViewMode && (
                            <td className="border p-2 text-center">
                              <button
                                onClick={() => removeTrainee(trainee.traineeId)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove trainee"
                              >
                                <X size={16} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={isViewMode ? 3 : 4}
                          className="text-center p-4"
                        >
                          <div className="flex flex-col items-center text-gray-500">
                            <X className="text-2xl" />
                            <span>NO TRAINEES SELECTED</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-center">
                    <div>
                      <p className="text-sm text-gray-700">
                        Page <span className="font-medium">{currentPage}</span>{" "}
                        of <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex bg-[#61748E] justify-end gap-4 px-2 py-2 mt-6">
        {step > 1 && (
          <button
            className="bg-[#869ab6] text-white px-6 py-2 rounded-full"
            onClick={handlePrevious}
            disabled={isSubmitting || uploading}
          >
            Previous
          </button>
        )}
        {step < 2 ? (
          <button
            className="bg-[#0fa9a6] text-white px-10 py-2 rounded-full disabled:opacity-50"
            onClick={handleNext}
            disabled={isSubmitting || uploading || !isStep1Valid}
          >
            Next
          </button>
        ) : mode !== "view" ? (
          <button
            className="bg-[#0fa9a6] text-white px-10 py-2 rounded-full disabled:opacity-50"
            onClick={handleSubmit}
            disabled={isSubmitting || uploading}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        ) : null}
      </div>

      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Uploading Files</h3>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-[#0fa9a6] h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="mt-2 text-center">{uploadProgress}%</p>
          </div>
        </div>
      )}

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="bg-white w-[500px] rounded-xl flex flex-col justify-center items-center text-center h-full p-6">
            <img src={images.CheckMark} alt="Success" className="w-16" />
            <h4 className="text-xl font-semibold my-4">
              Successfully {mode === "edit" ? "updated" : "created"} training
            </h4>
            <div className="text-sm text-gray-600 mb-4 space-y-2">
              <p>✓ Training session has been scheduled</p>
              <p>✓ Email notifications sent to instructor and trainees</p>
            </div>
            <button
              className="px-6 bg-[#0fa9a6] text-white text-lg rounded-full py-2 hover:bg-[#0a8a87] transition-colors"
              onClick={handleModalContinue}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreateTraining;
